import { useCallback, useState } from 'react';
import { hexToRgb } from '../utils';
import useR05Api from '../api/useR05Api';
import { retry } from '../utils';
import i18n from 'i18next';
import { detectBrowserLanguage, getSupportedLanguages } from '../assets/languages/configs';

const useAppConfig = () => {
    const [isLoadingApp, setIsLoadingApp] = useState(false);
    const [shouldShowApp, setShouldShowApp] = useState(false);
    const [errorType, setErrorType] = useState<string | null>(null);
    const { fetchAppConfig } = useR05Api();

    /**
     * アプリ設定を反映させる。
     * @param {Object} configData
     * @returns
     */
    type AppConfig = {
        appName?: string;
        appNameShort?: string;
        mainColor?: string;
        subColor?: string;
        backgroundColor?: string;
        textColor?: string;
        borderColor?: string;
        inactiveColor?: string;
    };

    const applyAppConfig = useCallback((configData: AppConfig) => {
        if (configData == null) {
            return;
        }
        const { appName, appNameShort } = configData;
        // ページタイトル変更
        if (appName) {
            document.title = appName;
        }

        // manifest.json変更
        const myDynamicManifest = {
            short_name: appNameShort ?? appName,
            name: appName,
            icons: [
                {
                    src: '/icon-192x192.png',
                    sizes: '192x192',
                    type: 'image/png',
                },
                {
                    src: '/icon-256x256.png',
                    sizes: '256x256',
                    type: 'image/png',
                },
                {
                    src: '/icon-384x384.png',
                    sizes: '384x384',
                    type: 'image/png',
                },
                {
                    src: '/icon-512x512.png',
                    sizes: '512x512',
                    type: 'image/png',
                },
            ],
            start_url: window.location.origin,
            display: 'standalone',
            theme_color: '#ffffff',
            background_color: '#ffffff',
        };
        const blob = new Blob([JSON.stringify(myDynamicManifest)], { type: 'application/json' });
        document.querySelector('#my-manifest-placeholder')?.setAttribute('href', URL.createObjectURL(blob));

        //色設定反映
        const { mainColor, subColor, backgroundColor, textColor, borderColor, inactiveColor } = configData;
        if (mainColor) {
            document.documentElement.style.setProperty('--main-color', mainColor);
        }
        if (subColor) {
            document.documentElement.style.setProperty('--sub-color', subColor);
        }
        if (backgroundColor) {
            document.documentElement.style.setProperty('--background-color', backgroundColor);
            const { r, g, b } = hexToRgb(backgroundColor);
            document.documentElement.style.setProperty('--background-color-rgb', `${r}, ${g}, ${b}`);
        }
        if (textColor) {
            document.documentElement.style.setProperty('--text-color', textColor);
        }
        if (borderColor) {
            document.documentElement.style.setProperty('--border-color', borderColor);
        }
        if (inactiveColor) {
            document.documentElement.style.setProperty('--inactive-color', inactiveColor);
        }

        // 言語設定を行う
        const detectedLanguage = detectBrowserLanguage(true); //localStorageに保持した言語設定を参照する
        const supportedLanguages = getSupportedLanguages();
        const applyLanguage = supportedLanguages.includes(detectedLanguage) ? detectedLanguage : 'en';
        localStorage.setItem('lang', applyLanguage);
        void i18n.changeLanguage(applyLanguage);
        return;
    }, []);

    /**
     * アプリ設定
     */
    const configApp = useCallback(async () => {
        let id: NodeJS.Timeout | undefined = undefined;
        try {
            const localAppConfig = localStorage.getItem('APP_CONFIG') ?? null;
            if (localAppConfig) {
                applyAppConfig(JSON.parse(localAppConfig));
                setShouldShowApp(true);
            } else {
                setIsLoadingApp(true);
            }
            setErrorType(null);
            if (navigator.onLine) {
                id = setTimeout(() => {
                    const localAppConfig = localStorage.getItem('APP_CONFIG') ?? null;
                    if (!localAppConfig) {
                        console.error('timeout without app config');
                        setIsLoadingApp(false);
                        setErrorType('timeout');
                        setShouldShowApp(false);
                    }
                }, 30 * 1000);
                try {
                    // アプリ設定を取得する
                    // 3回リトライする
                    const newAppConfig = await retry(async () => {
                        const res = await fetchAppConfig();
                        return res && 'data' in res ? res.data : res;
                    }, 3);
                    if (newAppConfig) {
                        localStorage.setItem('APP_CONFIG', JSON.stringify({ ...newAppConfig }));
                        applyAppConfig(newAppConfig);
                        setErrorType(null);
                        setShouldShowApp(true);
                    }
                } catch {
                    // アプリ設定の取得に失敗してもローカルにある場合は、ローカルのアプリ設定を適用する
                    const localAppConfig = localStorage.getItem('APP_CONFIG') ?? null;
                    if (localAppConfig) {
                        applyAppConfig(JSON.parse(localAppConfig));
                        setShouldShowApp(true);
                    } else {
                        throw new Error('failed to fetch app config');
                    }
                }
            }
        } catch (error) {
            console.error(error);
            setShouldShowApp(false);
        } finally {
            if (id !== undefined) {
                clearTimeout(id);
            }
            setIsLoadingApp(false);
        }
    }, [fetchAppConfig, applyAppConfig]);

    /**
     * 使用デバイスにホームバーがあるかどうかを判定する。
     * 新規のiPhoneはホームバーがあると考えて、ブラックリスト方式で判定する。
     * @returns
     */
    const existsHomeBar = useCallback(() => {
        // iPhoneで、ホームバーがない機種一覧
        const noHomeBarDevices = [
            {
                version: '1,2,3,4',
                height: 480,
                width: 320,
            },
            {
                version: '5,6',
                height: 568,
                width: 320,
            },
            {
                version: '7,8,SE',
                height: 667,
                width: 375,
            },
            {
                version: '6,7(Plus)',
                height: 736,
                width: 414,
            },
        ];

        const screenHeight = window.screen.height;
        const screenWidth = window.screen.width;
        // console.log(screenHeight, screenWidth);
        const notExistHomeBar = noHomeBarDevices.some(
            (device) => device.height === screenHeight && device.width === screenWidth,
        );

        return !notExistHomeBar;
    }, []);

    const setFillHeight = useCallback(() => {
        const fullHeight = document.documentElement.clientHeight;
        const isPwaMode = window.matchMedia('(display-mode: standalone)').matches;
        const isIphonePWA = window.navigator.userAgent.includes('iPhone') && isPwaMode;
        const viewportMetaTag = document.querySelector('meta[name="viewport"]');
        let bottomBarHeight = 0;
        if (isIphonePWA) {
            if (existsHomeBar()) {
                bottomBarHeight = 34;
            } else {
                viewportMetaTag?.setAttribute(
                    'content',
                    'width=device-width, initial-scale=1, viewport-fit=auto, maximum-scale=1.0',
                );
            }
        }

        const appHeight = fullHeight - bottomBarHeight;
        const vh = appHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        document.documentElement.style.setProperty('--screen-height', `${fullHeight}px`);
    }, [existsHomeBar]);

    return { isLoadingApp, shouldShowApp, errorType, setFillHeight, configApp };
};

export default useAppConfig;
